import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Button, CheckboxProps, FormField, Header, Label, Segment } from "semantic-ui-react";
import { Game } from "../../../app/models/Game";
import { useStore } from "../../../app/stores/store";
import { observer } from "mobx-react-lite";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { v4 as uuid } from "uuid";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from 'yup';
import MyInputText from "../../../app/common/form/MyTextInput";
import MyTextArea from "../../../app/common/form/MyTextArea";
import MySelectInput from "../../../app/common/form/MySelectInput";
import { sportOptions } from "../../../app/common/options/sportOptions";
import MyDateInput from "../../../app/common/form/MyDateInput";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import { GameLocationPicker } from "../../../components/map/GameLocationPicker";
import Select, { SingleValue } from 'react-select';
import AvailableSlotsModal from "./AvailableSlotsModal";
import { AvailableSlotsRequestDTO, AvailableSlotDTO, TimeSlot } from "../../../app/models/AvailableGameSlots";
import { useTranslation } from 'react-i18next';

// Create localized options helper function
const createLocalizedOptions = (optionsConfig: any[], t: any, translationKey: string) => {
    return optionsConfig.map(option => ({
        ...option,
        text: t(`${translationKey}.${option.key}`) || option.text
    }));
};

// Enhanced MyInputText component with required field highlighting
const MyInputTextWithRequired = ({ name, label, placeholder, required = false, ...props }: any) => {
    const { values } = useFormikContext<any>();
    const isEmpty = !values[name] || values[name].toString().trim() === '';
    const shouldShowRed = required && isEmpty;
    
    return (
        <div className={shouldShowRed ? 'required-field-wrapper' : ''}>
            <MyInputText 
                name={name}
                placeholder={placeholder}
                label={
                    <span style={{ color: shouldShowRed ? '#e74c3c' : undefined }}>
                        {label}
                        {required && <span style={{ color: '#e74c3c', marginLeft: '4px' }}>*</span>}
                    </span>
                }
                style={{
                    ...props.style,
                    ...(shouldShowRed && {
                        border: '2px solid #e74c3c',
                        backgroundColor: '#fef9f9'
                    })
                }}
                {...props}
            />
        </div>
    );
};

// Enhanced MyTextArea component with required field highlighting
const MyTextAreaWithRequired = ({ name, label, placeholder, required = false, ...props }: any) => {
    const { values } = useFormikContext<any>();
    const isEmpty = !values[name] || values[name].toString().trim() === '';
    const shouldShowRed = required && isEmpty;
    
    return (
        <div className={shouldShowRed ? 'required-field-wrapper' : ''}>
            <MyTextArea 
                name={name}
                placeholder={placeholder}
                label={
                    <span style={{ color: shouldShowRed ? '#e74c3c' : undefined }}>
                        {label}
                        {required && <span style={{ color: '#e74c3c', marginLeft: '4px' }}>*</span>}
                    </span>
                }
                style={{
                    ...props.style,
                    ...(shouldShowRed && {
                        border: '2px solid #e74c3c',
                        backgroundColor: '#fef9f9'
                    })
                }}
                {...props}
            />
        </div>
    );
};

// Enhanced MySelectInput component with required field highlighting
const MySelectInputWithRequired = ({ name, label, placeholder, required = false, ...props }: any) => {
    const { values } = useFormikContext<any>();
    const isEmpty = !values[name] || values[name].toString().trim() === '';
    const shouldShowRed = required && isEmpty;
    
    return (
        <div className={shouldShowRed ? 'required-field-wrapper' : ''}>
            <MySelectInput 
                name={name}
                placeholder={placeholder}
                label={
                    <span style={{ color: shouldShowRed ? '#e74c3c' : undefined }}>
                        {label}
                        {required && <span style={{ color: '#e74c3c', marginLeft: '4px' }}>*</span>}
                    </span>
                }
                style={{
                    ...props.style,
                    ...(shouldShowRed && {
                        border: '2px solid #e74c3c',
                        backgroundColor: '#fef9f9'
                    })
                }}
                {...props}
            />
        </div>
    );
};

// Enhanced MyDateInput component with required field highlighting
const MyDateInputWithRequired = ({ name, label, placeholder, required = false, ...props }: any) => {
    const { values } = useFormikContext<any>();
    const isEmpty = !values[name];
    const shouldShowRed = required && isEmpty;
    
    return (
        <div className={shouldShowRed ? 'required-field-wrapper' : ''}>
            <MyDateInput 
                name={name}
                placeholder={placeholder}
                label={
                    <span style={{ color: shouldShowRed ? '#e74c3c' : undefined }}>
                        {label}
                        {required && <span style={{ color: '#e74c3c', marginLeft: '4px' }}>*</span>}
                    </span>
                }
                style={{
                    ...props.style,
                    ...(shouldShowRed && {
                        border: '2px solid #e74c3c',
                        backgroundColor: '#fef9f9'
                    })
                }}
                {...props}
            />
        </div>
    );
};

// Enhanced Select component with required field highlighting
const SelectWithRequired = ({ name, label, placeholder, options, required = false }: any) => {
    const { values, setFieldValue, errors, touched } = useFormikContext<any>();
    const isEmpty = !values[name] || values[name].toString().trim() === '';
    const shouldShowRed = required && isEmpty;
    
    return (
        <div className={shouldShowRed ? 'required-field-wrapper' : ''}>
            <div style={{ marginBottom: "15px" }}>
                <label style={{ 
                    marginBottom: "5px", 
                    display: "block",
                    color: shouldShowRed ? '#e74c3c' : undefined,
                    fontWeight: shouldShowRed ? '600' : 'normal'
                }}>
                    {label}
                    {required && <span style={{ color: '#e74c3c', marginLeft: '4px' }}>*</span>}
                </label>
                <Select
                    options={options}
                    value={options.find((option: any) => option.value === values[name]) || null}
                    onChange={(option) => setFieldValue(name, option?.value || "")}
                    placeholder={placeholder}
                    isSearchable
                    styles={{
                        control: (base) => ({
                            ...base,
                            ...(shouldShowRed && {
                                borderColor: '#e74c3c',
                                borderWidth: '2px',
                                backgroundColor: '#fef9f9',
                                '&:hover': {
                                    borderColor: '#c0392b'
                                }
                            })
                        })
                    }}
                />
                {errors[name] && touched[name] && (
                    <div className="error-message" style={{ color: "red", marginTop: "5px" }}>
                        ///
                    </div>
                )}
            </div>
        </div>
    );
};

export default observer(function GameForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { gameStore } = useStore();
    const { createGame, updateGame, loading, loadGame, loadingInitial } = gameStore;
    const { id } = useParams<{ id: string }>();
    
    const location = useLocation();
    
    const [showAvailableSlotsModal, setShowAvailableSlotsModal] = useState(false);
    const [availableSlotsRequest, setAvailableSlotsRequest] = useState<AvailableSlotsRequestDTO | null>(null);
    
    const [game, setGame] = useState<Partial<Game>>({
        id: '',
        gameName: '',
        gameDescription: '',
        gameLocationID: '',
        sportsID: '',
        gameTypeID: '',
        gamePaymentTypeID: '',
        dateTimeStart: null,
        dateTimeEnd: null,
        maxNumberOfParticipants: 0,
        isPayedFor: false,
        hiddenInSearch: false,
        gamePrice: 0,
        adminContactDetail: '',
        inclusionID: '',
        companySportVenueID: '',
        latitude: 0,
        longitude: 0,
        gamePaymentType: null,
        gameType: null,
        inclusion: null,
        sport: null,
        createdAt: '', 
        modifiedAt: '', 
        modifiedBy: '',
        isDeleted: 0     
    });   
    
    const gameTypeOptions = [
        { text: t('gameTypes.private'), value: "416b6b95-5d42-4b6c-8711-0fd8e8bdee87" },
        { text: t('gameTypes.public'), value: "958a15e1-7ac7-444e-8292-ea1eeb4f85ba" }
    ];

    const gamePaymentTypeOptions = [
        { text: t('gamePaymentTypes.free'), value: "1296b786-c6de-45f9-8363-552112605904" },
        { text: t('gamePaymentTypes.payed'), value: "88f7193d-72e5-4ddd-aff6-1f9b25ab0a56" }
    ];

    const inclusionTypeOptions = [
        { text: t('inclusionTypes.unrestricted'), value: "780295b5-8fbe-42f0-80a9-bde0805d3be4" },
        { text: t('inclusionTypes.mixedAbility'), value: "6aaa5f46-c815-452b-8693-0009645982d8" },
        { text: t('inclusionTypes.restricted'), value: "1e41a459-47cc-43a3-b186-b0ca26baa814" }
    ];

    const localizedSportOptions = sportOptions.map(option => ({
        ...option,
        label: t(`sports.${option.label.toLowerCase()}`) || option.label
    }));

    const validationSchema = Yup.object({
      gameName: Yup.string().required(t('gameForm.validation.gameNameRequired')),
      gameDescription: Yup.string().required(t('gameForm.validation.gameDescriptionRequired')),
      sportsID: Yup.string().required(t('gameForm.validation.sportRequired')),
      gameTypeID: Yup.string().required(t('gameForm.validation.gameTypeRequired')),
      gamePaymentTypeID: Yup.string().required(t('gameForm.validation.gamePaymentTypeRequired')),
      adminContactDetail: Yup.string().required(t('gameForm.validation.adminContactRequired')),
      dateTimeStart: Yup.date()
        .nullable()
        .required(t('gameForm.validation.gameStartTimeRequired')),
      dateTimeEnd: Yup.date()
        .nullable()
        .required(t('gameForm.validation.gameEndTimeRequired'))
        .test({
          name: 'end-after-start',
          exclusive: true,
          message: t('gameForm.validation.endTimeAfterStart'),
          test: function(endDate) {
            const { dateTimeStart } = this.parent;
            if (!dateTimeStart || !endDate) return true;
            return new Date(endDate).getTime() > new Date(dateTimeStart).getTime();
          }
        }),
      inclusionID: Yup.string().required(t('gameForm.validation.inclusionTypeRequired')),
    });

    useEffect(() => {
        const initializeGame = async () => {
            const searchParams = new URLSearchParams(location.search);
            const duplicateId = searchParams.get('duplicate');
            
            if (id) {
                const loadedGame = await loadGame(id);
                setGame(current => ({
                    ...current,
                    ...loadedGame,
                }));
            } else if (duplicateId) {
                const templateGame = await loadGame(duplicateId);
                
                setGame(current => ({
                    ...current,
                    gameName: templateGame?.gameName,
                    gameDescription: templateGame?.gameDescription,
                    gameLocationID: templateGame?.gameLocationID,
                    sportsID: templateGame?.sportsID,
                    gameTypeID: templateGame?.gameTypeID,
                    gamePaymentTypeID: templateGame?.gamePaymentTypeID,
                    maxNumberOfParticipants: templateGame?.maxNumberOfParticipants,
                    isPayedFor: templateGame?.isPayedFor,
                    hiddenInSearch:templateGame?.hiddenInSearch,
                    gamePrice: templateGame?.gamePrice,
                    inclusionID: templateGame?.inclusionID,
                    companySportVenueID: templateGame?.companySportVenueID,
                    latitude: templateGame?.latitude,
                    longitude: templateGame?.longitude,
                    
                    id: '',
                    adminContactDetail: '',
                    dateTimeStart: null,
                    dateTimeEnd: null
                }));
            } else {
                setGame(current => ({
                    ...current,
                    ...getDefaultGame(),
                }));
            }
        };
    
        initializeGame();
    }, [id, loadGame, location.search]);

    const getDefaultGame = () => ({
      id: '',
      gameName: '',
      gameDescription: '',
      gameLocationID: '',
      sportsID: '',
      gameTypeID: '',
      gamePaymentTypeID: '',
      dateTimeStart: null,
      dateTimeEnd: null,
      maxNumberOfParticipants: 0,
      isPayedFor: false,
      hiddenInSearch: false,
      gamePrice: 0,
      adminContactDetail: '',
      inclusionID: '',
      companySportVenueID: '',
      latitude: 0,
      longitude: 0,
      gamePaymentType: null,
      gameType: null,
      inclusion: null,
      sport: null,
      createdAt: '',
      modifiedAt: '',
      modifiedBy: '',
      isDeleted: 0,
    });

    function handleFormSubmit(game: Partial<Game>) {
        const selectedSport = localizedSportOptions.find(option => option.value === game.sportsID);
        const sportName = selectedSport ? selectedSport.label : "";

        if (!game.id) {
            let newGame = {
                ...game,
                id: uuid(),
                sport: {
                  id: game.sportsID!,
                  sportName: sportName
              }
            };
            createGame(newGame as Game).then(() => {
                navigate(`/games/${newGame.id}`);
            });
        } else {
          let updatedGame = {
            ...game,
            sport: {
              id: game.sportsID!,
              sportName: sportName
            }
          };
          updateGame(updatedGame as Game).then(() => {
              navigate(`/games/${updatedGame.id}`);
          });
        }
    }

    function handleCheckboxChange(event: FormEvent<HTMLInputElement>, data: CheckboxProps) {
        const { name, checked } = data;
        setGame({ ...game, [name as string]: checked });
    }

    const handleFindAvailableSlots = (formValues: Partial<Game>) => {
        if (!formValues.sportsID || !formValues.dateTimeStart || !formValues.latitude || !formValues.longitude) {
            alert(t('gameForm.alerts.fillRequiredFields'));
            return;
        }
        
        const request: AvailableSlotsRequestDTO = {
            sportID: formValues.sportsID,
            gameStartDateTime: new Date(formValues.dateTimeStart),
            latitude: formValues.latitude,
            longitude: formValues.longitude,
            radiusKm: 15
        };

        setAvailableSlotsRequest(request);
        setShowAvailableSlotsModal(true);
    };

    const handleSlotSelect = (slot: AvailableSlotDTO, timeSlot: TimeSlot, setFieldValue: any) => {
        setGame(prev => ({
            ...prev,
            companySportVenueID: slot.companySportVenueAssetId || '',
            latitude: slot.venueLatitude || prev.latitude,
            longitude: slot.venueLongitude || prev.longitude
        }));

        const gameDate = new Date(game.dateTimeStart!);
        const [startHour, startMinute] = timeSlot.startTime.split(':').map(Number);
        const [endHour, endMinute] = timeSlot.endTime.split(':').map(Number);

        const startDateTime = new Date(gameDate);
        startDateTime.setHours(startHour, startMinute, 0, 0);

        const endDateTime = new Date(gameDate);
        endDateTime.setHours(endHour, endMinute, 0, 0);

        setFieldValue('dateTimeStart', startDateTime);
        setFieldValue('dateTimeEnd', endDateTime);

        setShowAvailableSlotsModal(false);
    };

    if(loadingInitial || !game) return <LoadingComponent content={t('gameForm.loading.loadingGame')}/>;

    return(
        <Segment clearing>
          <style>{`
            .required-field-wrapper .ui.input > input:focus,
            .required-field-wrapper .ui.dropdown:focus,
            .required-field-wrapper textarea:focus {
              border-color: #c0392b !important;
              background-color: #fff !important;
              box-shadow: 0 0 8px rgba(231, 76, 60, 0.2) !important;
            }
            
            .required-field-wrapper .ui.input > input,
            .required-field-wrapper .ui.dropdown,
            .required-field-wrapper textarea {
              transition: all 0.3s ease !important;
            }
          `}</style>
          
          <Header content={t('gameForm.headers.gameDetails')} sub color="teal"/>
          <Formik enableReinitialize initialValues={game} onSubmit={(values) => handleFormSubmit(values)} validationSchema={validationSchema}>
          {({handleSubmit, setFieldValue, isValid, isSubmitting, dirty, values})=>
              (
              <Form className="ui form error" onSubmit={handleSubmit}>
              <MyInputTextWithRequired 
                name='gameName' 
                placeholder={t('gameForm.placeholders.gameName')} 
                label={t('gameForm.labels.gameName')}
                required={true}
              />
              <MyTextAreaWithRequired 
                rows={4} 
                placeholder={t('gameForm.placeholders.gameDescription')} 
                name='gameDescription' 
                label={t('gameForm.labels.gameDescription')}
                required={true}
              />          
              <SelectWithRequired
                name="sportsID"
                label={t('gameForm.labels.sport')}
                placeholder={t('gameForm.placeholders.sport')}
                options={localizedSportOptions}
                required={true}
              />
              <MySelectInputWithRequired 
                options={gameTypeOptions} 
                placeholder={t('gameForm.placeholders.gameType')} 
                name='gameTypeID' 
                label={t('gameForm.labels.gameType')}
                required={true}
              />
              <MyCheckbox 
                label={t('gameForm.checkboxes.hiddenInSearch')} 
                name="hiddenInSearch" 
              />
              <MySelectInputWithRequired 
                options={gamePaymentTypeOptions} 
                placeholder={t('gameForm.placeholders.gamePaymentType')} 
                name='gamePaymentTypeID' 
                label={t('gameForm.labels.gamePaymentType')}
                required={true}
              />
              <MyInputText 
                type='number'  
                defaultValue={0} 
                placeholder={t('gameForm.placeholders.gamePrice')} 
                name='gamePrice' 
                label={t('gameForm.labels.gamePrice')}
              />
              <MyInputText 
                placeholder={t('gameForm.placeholders.maxParticipants')} 
                name='maxNumberOfParticipants' 
                label={t('gameForm.labels.maxParticipants')}
              />
              <MyInputTextWithRequired 
                placeholder={t('gameForm.placeholders.adminContact')} 
                name='adminContactDetail' 
                label={t('gameForm.labels.adminContact')}
                required={true}
              />
              <MySelectInputWithRequired 
                options={inclusionTypeOptions} 
                placeholder={t('gameForm.placeholders.inclusionType')} 
                name='inclusionID' 
                label={t('gameForm.labels.inclusionType')}
                required={true}
              />   
              <MyCheckbox 
                label={t('gameForm.checkboxes.isPayedFor')} 
                name="isPayedFor" 
              />
              <Header content={t('gameForm.headers.locationDetails')} sub color="teal"/>
              <GameLocationPicker
                  onLocationSelect={(lat, lng) => {
                      setFieldValue('latitude', lat);
                      setFieldValue('longitude', lng);
                  }}
                  initialLat={game.latitude}
                  initialLng={game.longitude}
              />
              <Header content={t('gameForm.headers.timeDetails')} sub color="teal"/>
              <MyDateInputWithRequired 
                placeholder={t('gameForm.placeholders.gameStartTime')} 
                name='dateTimeStart' 
                label={t('gameForm.labels.gameStartTime')}
                required={true}
              />
              <MyDateInputWithRequired 
                placeholder={t('gameForm.placeholders.gameEndTime')} 
                name='dateTimeEnd' 
                label={t('gameForm.labels.gameEndTime')}
                required={true}
              />
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                <div>
                <Button 
                    type='button'
                    color='blue'
                    icon='search'
                    floated='right'
                    content={t('gameForm.buttons.searchLocalVenues')}
                    onClick={() => handleFindAvailableSlots(values)}
                    style={{ marginLeft: '30px' }}
                />
                  <Button 
                    as={Link} 
                    to='/games' 
                    floated='right' 
                    type='button' 
                    content={t('gameForm.buttons.cancel')}
                  />
                  <Button 
                    disabled={isSubmitting || !isValid}
                    loading={loading} 
                    floated='right' 
                    positive 
                    type='submit' 
                    content={t('gameForm.buttons.submit')}
                    style={{ marginLeft: '10px' }}
                  />
                </div>
              </div>

              <AvailableSlotsModal
                  open={showAvailableSlotsModal}
                  onClose={() => setShowAvailableSlotsModal(false)}
                  onSlotSelect={(slot, timeSlot) => handleSlotSelect(slot, timeSlot, setFieldValue)}
                  requestData={availableSlotsRequest}
              />
              </Form>
              )       
              }
          </Formik>
        </Segment>
    )
})